import { formatNumber } from '@angular/common';
import { Injectable, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AttributeBase, Leg } from '@approot/models';
import {
  LegSyncService,
  RfqTabService,
  TradesCaptureService
} from '@approot/services';
import { Utilities } from '@approot/utilities';
import { LegScheduleService } from '@approot/widgets/strategy-detail/leg/services/leg-schedule.service';
import {
  debounceTime,
  distinctUntilChanged,
  pairwise,
  scan
} from 'rxjs/operators';
import {
  accumulatorDirectionChange,
  accumulatorMultiPeriodDirectionChange,
  accumulatorSpotSettledDirectionChange
} from '../utilities/accumulator-utilities';
import { clearFirstEditedDcdAttribute } from '../utilities/dcd-utilities';
import { koTypeChange } from '../utilities/knock-out-utilities';
import { getAttributeControlFromKey } from '../utilities/leg-attribute-utilities';
import {
  clearTextColor,
  dropDownSideInfoSetter,
  makeTextCustomColor,
  onMarginStyleChange,
  optionsChangeHandler,
  scheduleLegAttributeChange,
  setButtonText,
  wipeAttributeInfo
} from '../utilities/leg.utilities';
import {
  depositRateChanges,
  depositTakerChanges
} from '../utilities/trade-attribute-utilities';
import { LegBarrierFormListenersService } from './leg-barrier-form-listeners.service';
import { LegDateService } from './leg-date.service';
import { LegFwdVolService } from './leg-fwd-vol.service';
import { LegHelperService } from './leg-helper.service';
import { LegMiscellaneousService } from './leg-miscellaneous.service';
import { LegSpotStrikeService } from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegOtherFormListenersService {
  constructor(
    private legHelperService: LegHelperService,
    private legFwdVolService: LegFwdVolService,
    private legSpotStrikeService: LegSpotStrikeService,
    private rfqTabService: RfqTabService,
    private tradesCaptureService: TradesCaptureService,
    private renderer: Renderer2,
    private legScheduleService: LegScheduleService,
    private legDateService: LegDateService,
    private legSyncService: LegSyncService,
    private legMiscellaneousService: LegMiscellaneousService,
    private legBarrierFormListenersService: LegBarrierFormListenersService
  ) {}

  public otherListeners(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    this.wholeLegListener(form, leg);
    ['', '1', '2'].forEach(numberKey =>
      this.genericOptionTypeListener(numberKey, form, leg)
    );
    this.directionListener(form, leg);
    this.floatingLegSpreadBpsListener(form, leg);
    this.cutOffListeners(form);
    this.fixingSourceListener(form);
    this.spotListener(form);
    this.rfqAttributeListeners(form);
    this.basketListener(form, leg);
    this.optionsPortfolioListener(form, leg);
    this.optionsPortfolioSpotsListener(form, leg);
    this.depositTakerListener(form, leg);
    this.depositRateListener(form, leg);
    this.marginStyleRelatedListeners(form, leg);
    this.floatingLegIndexListener(form);
    this.conversionRateListener(form, leg);
    this.marginTypeListener(form);
    this.lkoListener(form, leg);
    this.secondLkoListener(form, leg);
    this.pivotListener(form, leg);
    this.premiumSettlementDateListener(form);
    this.premiumValueType(form, leg);
    this.knockOutListener(form, leg);
    this.leverageListeners(form, leg);
    this.marginStyleListener(form, leg);
    this.numberOfGuaranteedFlowsListener(form, leg);
  }

  private numberOfGuaranteedFlowsListener(form: FormGroup, leg: Leg): void {
    const isGuaranteedSettlementLeveragedCtrl = form.get(
      'isGuaranteedSettlementLeveraged'
    );
    this.legHelperService.subSink.sink = form
      .get('nbGuaranteedSettlements')
      ?.valueChanges.subscribe(val => {
        if (
          this.legHelperService.rfqExecutionSettings.rfqLeg &&
          leg.productType === 'simpleaccumulator'
        ) {
          if (val > 0) {
            isGuaranteedSettlementLeveragedCtrl?.enable();
          } else {
            isGuaranteedSettlementLeveragedCtrl?.disable();
            isGuaranteedSettlementLeveragedCtrl?.setValue('');
          }
        }
      });
  }
  private wholeLegListener(form: FormGroup, leg: Leg): void {
    this.formStatusChanges(form, leg);
    this.genericControlListeners(form, leg);
  }

  private formStatusChanges(form: FormGroup, leg: Leg): void {
    this.legHelperService.subSink.sink = form.statusChanges.subscribe(val => {
      this.legMiscellaneousService.legDetailsChanged(); // legs data has changed wipe data regarding the previous status
      this.legHelperService.onChangeInLegFn();
      if (val === 'VALID') {
        // if the status changes of the form to being valid
        this.accumulatorSpotSettledOptimizer(leg, form);
        this.simpleAccumulatorOptimizer(leg, form);
      }

      if (
        this.legHelperService.tradingMode &&
        (!leg.fromTab || this.legHelperService.presetsLoaded)
      ) {
        // if there are changes in the form, wipe the color of the tradeId
        makeTextCustomColor('tradeId', Utilities.whiteTextColor, leg);
      }
    });
  }

  private accumulatorSpotSettledOptimizer(leg: Leg, form: FormGroup): void {
    if (leg.productType === 'accumulatorspotsettled') {
      // in the case that the user has filled in the form with o for a barrier
      // then optimize these barriers
      if (
        form.get('upperBarrierType')?.value === 'UpAndOut' &&
        form.get('upperBarrier')?.value === 'o'
      ) {
        this.legMiscellaneousService.optimize('upperBarrier');
        // if we need to optimize the upperBarrier do so
      }

      if (
        form.get('lowerBarrierType')?.value === 'DownAndOut' &&
        form.get('lowerBarrier')?.value === 'o'
      ) {
        this.legMiscellaneousService.optimize('lowerBarrier');
        // if we need to optimize the lowerBarrier do so
      }
    }
  }

  private simpleAccumulatorOptimizer(leg: Leg, form: FormGroup): void {
    this.legBarrierFormListenersService.optimizeKoBarrier(form, leg);
  }
  private genericControlListeners(form: FormGroup, leg: Leg): void {
    Object.keys(form.controls).forEach(key => {
      const attributeCtrl = getAttributeControlFromKey(
        key,
        leg.attributesAsFormControls
      );
      this.legHelperService.subSink.sink = form
        .get(key)
        .valueChanges.pipe(pairwise())
        .subscribe(([oldVal, val]) => {
          this.attributesDisabledOnNone(val, oldVal, form, attributeCtrl);
        });
    });
  }

  private attributesDisabledOnNone(
    val: string,
    oldVal: string,
    form: FormGroup,
    attributeCtrl: AttributeBase<any>
  ): void {
    attributeCtrl.attributesDisabledOnNone?.forEach(disabledKey => {
      if (val === 'None') {
        form.get(disabledKey)?.setValue('');
        form.get(disabledKey)?.disable();
      } else if (oldVal === 'None') {
        form.get(disabledKey)?.enable();
      }
    });
  }
  private genericOptionTypeListener(
    optionTypeNumber: string,
    form: FormGroup,
    leg: Leg
  ) {
    if (form.get('optionType' + optionTypeNumber)) {
      this.legHelperService.subSink.sink = form
        .get('optionType' + optionTypeNumber)
        .valueChanges.subscribe(val => {
          this.onOptionTypeChange(val, optionTypeNumber, leg, form);
        });
    }
  }

  private onOptionTypeChange(
    val: string,
    optionTypeNumber: string,
    leg: Leg,
    form: FormGroup
  ): void {
    // to optionType add the side info to its dropDown menu

    this.legSpotStrikeService.onStrikeCalc(
      optionTypeNumber,
      leg.shortcutValueUsed[`strike${optionTypeNumber}`]
    );

    if (leg.baseCurrency && val && form.get('optionType' + optionTypeNumber)) {
      dropDownSideInfoSetter(
        'optionType' + optionTypeNumber,
        'on ' + leg.baseCurrency,
        leg
      );
    } else if (!val) {
      wipeAttributeInfo('optionType' + optionTypeNumber, leg);
    }
  }

  private directionListener(form: FormGroup, leg: Leg): void {
    this.legHelperService.subSink.sink = form
      .get('direction')
      ?.valueChanges.subscribe(val => {
        // to direction add the side info to its dropDown menu
        if (form.get('cross') && form.get('cross').value && val) {
          dropDownSideInfoSetter(
            'direction',
            leg.baseCurrency + '/' + leg.quoteCurrency,
            leg
          );
        } else if (!val) {
          wipeAttributeInfo('direction', leg);
        }
        accumulatorSpotSettledDirectionChange(val, form, leg);
        accumulatorDirectionChange(val, form, leg);
        accumulatorMultiPeriodDirectionChange(
          val,
          form,
          leg,
          this.legHelperService.presetsLoaded
        );
        this.legScheduleService.updateScheduleValidity('schedule');
      });
  }

  private floatingLegSpreadBpsListener(form: FormGroup, leg: Leg): void {
    if (form.get('floatingLegSpreadBps')) {
      this.legHelperService.subSink.sink = form
        .get('floatingLegSpreadBps')
        .valueChanges.subscribe(_ => {
          scheduleLegAttributeChange(
            form,
            leg,
            'floatingLegSpreadBps',
            'floatingLegSchedule'
          );
        });
    }
  }

  private cutOffListeners(form: FormGroup): void {
    if (form.get('cutOff')) {
      this.legHelperService.subSink.sink = form
        .get('cutOff')
        .valueChanges.subscribe(val => {
          this.legFwdVolService.calcImpliedVolatility('');
          this.legFwdVolService.calcImpliedVolatility('1');
          this.legFwdVolService.calcImpliedVolatility('2');
        });
    }
  }

  private fixingSourceListener(form: FormGroup): void {
    if (form.get('fixingSource')) {
      this.legHelperService.subSink.sink = form
        .get('fixingSource')
        .valueChanges.subscribe(val => {
          this.legFwdVolService.calcImpliedVolatility('');
          this.legFwdVolService.calcImpliedVolatility('1');
          this.legFwdVolService.calcImpliedVolatility('2');
          if (this.legHelperService.presetsLoaded) {
            this.legScheduleService.callScheduleGenerator();
          }
        });
    }
  }

  private spotListener(form: FormGroup): void {
    if (form.get('spot')) {
      this.legHelperService.subSink.sink = form
        .get('spot')
        .valueChanges.pipe(
          distinctUntilChanged(),
          scan(
            // Need to keep a count so we can handle the first update
            // which comes from a "phantom" event if the box is focussed, but not changed.
            // This only relevent when leg-syncing is active (RFQ mode) and is probably fragile.
            (acc, spot) => ({ count: acc.count + 1, spot }),
            { count: 0, spot: null }
          )
        )
        .subscribe({
          next: ({ count, spot }) => {
            this.legSpotStrikeService.onSpotChange(count, spot);
          }
        });
    }
  }

  private rfqAttributeListeners(form: FormGroup): void {
    ['spot', 'forwardPointsBid', 'forwardPointsAsk'].forEach(f => {
      if (!form.get(f)) {
        return;
      }
      this.legHelperService.subSink.sink = form
        .get(f)
        .valueChanges.pipe(distinctUntilChanged(), debounceTime(500))
        .subscribe({
          next: () => {
            this.rfqTabService.handleClientQuoteEventUsingLast(f);
          }
        });
    });
  }

  private basketListener(form: FormGroup, leg: Leg): void {
    if (form.get('basket')) {
      this.legHelperService.subSink.sink = form
        .get('basket')
        .valueChanges.subscribe(val => {
          setButtonText('basket', 'Currency Pairs', leg, form);

          let cross = val[0].cross; // cross of first basket

          if (leg.productType === 'europeanbasketoption') {
            // enable entry of expiration date when basket is filled
            if (form.get('basket')) {
              form.get('expiryDate').enable();
            }
          }
          if (form.get('cutOff')) {
            optionsChangeHandler(form, leg, 'cutOff', ['New York 10am'], '');
          }
          this.legMiscellaneousService.fixingSourcesCutOffsFetcher(form, leg);
        });
    }
  }

  private optionsPortfolioListener(form: FormGroup, leg: Leg): void {
    if (form.get('optionsPortfolio')) {
      this.legHelperService.subSink.sink = form
        .get('optionsPortfolio')
        .valueChanges.subscribe(() =>
          setButtonText('optionsPortfolio', 'Options', leg, form)
        );
    }
  }

  private optionsPortfolioSpotsListener(form: FormGroup, leg: Leg): void {
    if (form.get('optionsPortfolioSpots')) {
      this.legHelperService.subSink.sink = form
        .get('optionsPortfolioSpots')
        .valueChanges.subscribe(() =>
          setButtonText('optionsPortfolioSpots', 'Options Spots', leg, form)
        );
    }
  }

  private depositTakerListener(form: FormGroup, leg: Leg): void {
    const depositTakerAttribute = leg.attributes.find(
      attribute => attribute.shortName === 'depositTaker'
    );
    if (
      form.get('depositTaker') &&
      depositTakerAttribute &&
      !depositTakerAttribute.hide
    ) {
      this.legHelperService.subSink.sink = form
        .get('depositTaker')
        .valueChanges.subscribe(val => {
          depositTakerChanges(
            val,
            form,
            leg,
            this.tradesCaptureService.depositTakersAndCurrencies
          );
        });
    } else {
      // if depositTaker is found in hiddenAttributes
      const entities = this.tradesCaptureService.depositTakersAndCurrencies.map(
        depositTaker => depositTaker.entity
      );
      if (entities.length) {
        optionsChangeHandler(
          form,
          leg,
          'depositCurrency',
          this.tradesCaptureService.depositTakersAndCurrencies.map(
            depositTaker => depositTaker.currencies
          )[0],
          ''
        );
      }
    }
  }

  private depositRateListener(form: FormGroup, leg: Leg): void {
    if (form.get('depositRate')) {
      this.legHelperService.subSink.sink = form
        .get('depositRate')
        .valueChanges.subscribe(val => {
          depositRateChanges(form, leg, this.legHelperService.presetsLoaded);
          // add % to text box if val is there
          const percentageOrNot = val ? '%' : '';
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'depositRate'),
            form.get('depositRate'),
            percentageOrNot,
            !!percentageOrNot,
            'percentage',
            this.renderer
          );
        });
    }
  }

  private marginStyleRelatedListeners(form: FormGroup, leg: Leg): void {
    const marginStyleCtrl = form.get('marginStyle');
    ['retrocessionFee', 'margin', 'marginBid', , 'marginAsk'].forEach(field => {
      const ctrl = form.get(field);
      this.legHelperService.subSink.sink = ctrl?.valueChanges.subscribe(_ => {
        if (marginStyleCtrl?.enabled) {
          this.formatBasedOnMarginStyle(marginStyleCtrl.value, field);
        } else {
          if (
            field === 'margin' ||
            field === 'marginBid' ||
            field === 'marginAsk'
          ) {
            this.formatMarginBasedOnMarginType(form, leg);
          }
        }
      });
    });
  }

  private marginStyleListener(form: FormGroup, leg: Leg): void {
    this.legHelperService.subSink.sink = form
      .get('marginStyle')
      ?.valueChanges.subscribe(val => onMarginStyleChange(form, val, leg));
  }

  private formatMarginBasedOnMarginType(form: FormGroup, leg: Leg): void {
    const marginCtrl = form.get('margin');
    const isReadonly = getAttributeControlFromKey(
      'margin',
      leg.attributesAsFormControls
    ).readOnly;
    if (this.legHelperService.presetsLoaded && !isReadonly) {
      if (leg.missingAttributeOnPricing === 'margin') {
        // if we are changing the attribute that was missing
        clearTextColor(leg, 'margin'); // remove color from text
        leg.missingAttributeOnPricing = '';
      } else if (form.get(leg.missingAttributeOnPricing)) {
        // wipe the missing attribute value as its no longer correct
        form
          .get(leg.missingAttributeOnPricing)
          .setValue('', { emitEvent: false });
      }
      // tslint:disable-next-line: max-line-length
      clearFirstEditedDcdAttribute('margin', leg, form); // wipe values if more than 2 fields have values (margin, conversion rate and deposit rate)
    }

    // add % to text box if val is there
    if (form.get('marginType')?.value === '% p.a.') {
      // if margin is in percentage mode add percent at the end
      // convert margin into the apporiate format
      Utilities.endOfTextBoxAdder(
        this.legHelperService.qs('#' + 'margin'),
        marginCtrl,
        '%',
        true,
        'percentage',
        this.renderer
      );
    } else {
      // else we are in currency mode
      let value = Utilities.notionalCurrencyRemover(marginCtrl.value);
      if (!value || Utilities.isNumberFormatted(value, true)) {
        if (form.get('depositCurrency')) {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'margin'),
            marginCtrl,
            ' ' + form.get('depositCurrency').value,
            !!form.get('depositCurrency').value,
            'currency',
            this.renderer
          );
        }
      } else if (
        Utilities.isMillionShorthand(value, true) ||
        Utilities.isThousandShorthand(value, true)
      ) {
        marginCtrl.setValue(Utilities.shortHandNumberConverter(value, true));
      } else {
        if (!!value && new RegExp('^-?[0-9,]+$').test(value)) {
          let pureNumber = isNaN(value)
            ? Number(value.replace(/,/g, ''))
            : +value;
          marginCtrl.setValue(formatNumber(pureNumber, 'en-GB', '1.4-4'));
        }
      }
    }
  }

  private formatBasedOnMarginStyle(marginStyle: string, field: string): void {
    const form = this.legHelperService.form;
    const ctrl = form.get(field);
    if (!ctrl?.value) {
      return;
    }
    switch (marginStyle) {
      case 'Pips':
      case 'Percent Base':
      case 'Percent Term':
      case 'Vol Spread':
        const newEndOfText = marginStyle === 'Pips' ? ' pips' : '%';
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + field),
          ctrl,
          newEndOfText,
          true,
          marginStyle === 'Pips' ? 'pips' : 'percentage',
          this.renderer
        );
        break;
      default:
        break;
    }
  }

  private floatingLegIndexListener(form: FormGroup): void {
    if (form.get('floatingLegIndex')) {
      this.legHelperService.subSink.sink = form
        .get('floatingLegIndex')
        .valueChanges.subscribe(val => {
          if (form.get('swapStartDate') && form.get('swaptionSettlementDate')) {
            this.legScheduleService.rateIndexValueDateFromSetDate({
              setDate1: 'swapStartDate',
              setDate2: 'swaptionSettlementDate',
              dateToUse: 'swaptionExpiryDate'
            });
          } else if (form.get('swapStartDate')) {
            this.legScheduleService.rateIndexValueDateFromSetDate({
              setDate1: 'swapStartDate'
            });
          }
        });
    }
  }

  private conversionRateListener(form: FormGroup, leg: Leg): void {
    if (form.get('conversionRate')) {
      this.legHelperService.subSink.sink = form
        .get('conversionRate')
        .valueChanges.subscribe(_ => {
          const isReadonly = getAttributeControlFromKey(
            'conversionRate',
            leg.attributesAsFormControls
          ).readOnly;
          if (this.legHelperService.presetsLoaded && !isReadonly) {
            if (leg.missingAttributeOnPricing === 'conversionRate') {
              // if we are changing the attribute that was missing
              clearTextColor(leg, 'conversionRate'); // remove color from text
              leg.missingAttributeOnPricing = '';
            } else if (form.get(leg.missingAttributeOnPricing)) {
              // wipe the missing attribute value as its no longer correct
              form
                .get(leg.missingAttributeOnPricing)
                .setValue('', { emitEvent: false });
            }
            clearFirstEditedDcdAttribute('conversionRate', leg, form);
            // wipe values if more than 2 fields have values (margin, conversion rate and deposit rate)
          }
        });
    }
  }

  private marginTypeListener(form: FormGroup): void {
    if (form.get('marginType')) {
      this.legHelperService.subSink.sink = form
        .get('marginType')
        .valueChanges.subscribe(_ => {
          form.get('margin').setValue(''); // reset margin
        });
    }
  }

  private lkoListener(form: FormGroup, leg: Leg): void {
    if (form.get('lko')) {
      this.legHelperService.subSink.sink = form
        .get('lko')
        .valueChanges.subscribe(val => {
          scheduleLegAttributeChange(form, leg, 'lko', 'schedule');
        });
    }
  }

  private secondLkoListener(form: FormGroup, leg: Leg): void {
    if (form.get('secondLko')) {
      this.legHelperService.subSink.sink = form
        .get('secondLko')
        .valueChanges.subscribe(val => {
          scheduleLegAttributeChange(form, leg, 'secondLko', 'schedule');
        });
    }
  }

  private pivotListener(form: FormGroup, leg: Leg): void {
    if (form.get('pivot')) {
      this.legHelperService.subSink.sink = form
        .get('pivot')
        .valueChanges.subscribe(val => {
          scheduleLegAttributeChange(form, leg, 'pivot', 'schedule');
        });
    }
  }

  private premiumSettlementDateListener(form: FormGroup): void {
    if (form.get('premiumSettlementDate')) {
      const f = form.get('premiumSettlementDate');
      this.legHelperService.subSink.sink = f.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(250))
        .subscribe(() => {
          this.legDateService.calculateFuturePremiumSettlementDate();
        });
    }
  }

  private premiumValueType(form: FormGroup, leg: Leg): void {
    if (form.get('premiumValueType')) {
      const f = form.get('premiumValueType');
      this.legHelperService.subSink.sink = f.valueChanges.subscribe(val => {
        this.legSyncService.syncPremiumType(
          this.legHelperService.legIndex,
          val
        );
        if (
          val === 'Spot' &&
          this.legHelperService.legIndex !== 0 &&
          this.legSyncService.premiumValueTypes$.value[0].value === 'Spot'
        ) {
          // no need to calculate spot, if first leg has a spot
          const attribute = getAttributeControlFromKey(
            'premiumSettlementDate',
            leg.attributesAsFormControls
          );

          const premiumSettlementDateCtrl = form.get('premiumSettlementDate');
          const date =
            this.legSyncService.premiumSettlementDates$.value[0].value;
          premiumSettlementDateCtrl.setValue(date, { emitEvent: false });
          this.legDateService.premiumSettlementDateChange(date);
          attribute.readOnly = true;
        } else if (val === 'Spot') {
          this.legDateService.premiumValueTypeSpotDateCall(val);
        } else {
          this.legDateService.copySettlementDateToPremiumDate();
        }
      });
    }
  }

  private leverageListeners(form: FormGroup, leg: Leg): void {
    [
      'leverage',
      'leverageLevel',
      'secondLeverage',
      'leverageLong',
      'leverageShort'
    ].forEach(key => {
      if (form.get(key)) {
        this.legHelperService.subSink.sink = form
          .get(key)
          .valueChanges.subscribe(() => {
            scheduleLegAttributeChange(form, leg, key, 'schedule');
          });
      }
    });
  }

  private knockOutListener(form: FormGroup, leg: Leg): void {
    if (form.get('koType')) {
      this.legHelperService.subSink.sink = form
        .get('koType')
        .valueChanges.subscribe(val => {
          koTypeChange(val, form, leg);
        });
    }
  }
}
